import type { SelectOption } from 'v5/core/form-schema/formSchema.types';
import { FormField, FormMessage } from 'v5/design-system/Form';
import { InputGroup, InputGroupButton } from 'v5/design-system/InputGroup';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { cn } from 'v5/platform/dom/cn';
import { Outlet } from 'v5/platform/react/outlet/Outlet';
import { OutletProvider } from 'v5/platform/react/outlet/OutletContext';
import { OutletInject } from 'v5/platform/react/outlet/OutletInject';
import { useFieldContext } from '../FieldContext';
import { fieldAnchor } from '../unitPath';
import { partPlaceholder } from './EmptyField';
import { ghostRowClassName } from './listLayout';
import { NumberGroupInput } from './numberInput';

export type LegacyMeasurementInputProps = {
  /** RHF path of a `LegacyMeasurementValue` (`{ amount, unit }`). */
  path: string;
  /** Used in validation messages and accessible names. */
  label: string;
  required: boolean;
  units: SelectOption[];
  placeholder: string;
  className?: string;
};

/**
 * One `{ amount, unit }` row (legacy `MeasurementInput`): a number input with
 * a unit select appended, shared by the dimension and legacy measurement
 * fields. Validation messages surface below the group through the outlet.
 */
export function LegacyMeasurementInput({
  path,
  label,
  required,
  units,
  placeholder,
  className
}: LegacyMeasurementInputProps) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';

  return (
    <OutletProvider>
      <div className={cn('min-w-0', className)}>
        <InputGroup
          size={ctx.size}
          variant={ctx.variant}
          className={cn(ctx.ghost && ghostRowClassName)}
          aria-readonly={readOnly}
        >
          <FormField
            name={`${path}.amount`}
            render={({ field }) => (
              <>
                <NumberGroupInput
                  readOnly={readOnly}
                  placeholder={partPlaceholder(
                    ctx.mode,
                    placeholder,
                    (field.value ?? null) === null
                  )}
                  aria-label={label}
                  {...field}
                  {...fieldAnchor(field.name)}
                />
                <OutletInject>
                  <FormMessage />
                </OutletInject>
              </>
            )}
          />
          <FormField
            name={`${path}.unit`}
            render={({ field }) => (
              <>
                <Select
                  name={field.name}
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                >
                  <InputGroupButton asChild>
                    <SelectTrigger
                      variant="headless"
                      className="w-auto"
                      readOnly={readOnly}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      aria-label={`${label} unit`}
                      {...fieldAnchor(field.name)}
                    >
                      <SelectValue
                        placeholder={partPlaceholder(
                          ctx.mode,
                          'Unit',
                          (field.value ?? '') === ''
                        )}
                      />
                    </SelectTrigger>
                  </InputGroupButton>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <OutletInject>
                  <FormMessage />
                </OutletInject>
              </>
            )}
          />
        </InputGroup>
        <Outlet className="empty:contents p-2" />
      </div>
    </OutletProvider>
  );
}
